import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLine } from "../motion/Reveal";
import { MarketBackdrop } from "./MarketBackdrop";
import { IMAGES } from "../../data/content";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPortrait = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // subtle 3D tilt following the cursor
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 10);
    rx.set(-py * 10);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  const scroll = (href) => {
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -10 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" ref={ref} className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      <MarketBackdrop />
      {/* spotlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(60% 50% at 60% 20%, rgba(197,160,89,0.10), transparent 70%)" }}
      />

      <div className="container-editorial relative z-10 grid lg:grid-cols-12 gap-12 items-center w-full">
        {/* Left — copy */}
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="overline mb-8 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-gold inline-block" /> Trading · Discipline · Transformation
          </motion.p>

          <h1 className="font-serif font-medium text-[clamp(2.6rem,7vw,6.2rem)] leading-[0.95] tracking-tight text-bone">
            <MaskLine delay={0.35}>Trading the</MaskLine>
            <MaskLine delay={0.5}>
              <span className="italic text-gold">Markets.</span>
            </MaskLine>
            <MaskLine delay={0.65}>Transforming</MaskLine>
            <MaskLine delay={0.8}>
              <span className="italic">Life.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="mt-8 max-w-xl text-base md:text-lg text-ash leading-relaxed"
          >
            Dakka Gabriel Rey explores the intersection of financial markets, personal
            discipline, and meaningful transformation. His journey is built on knowledge,
            experience, resilience, and the belief that sustainable success begins from within.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button data-testid="hero-journey-btn" onClick={() => scroll("#journey")} className="btn-primary">
              Discover My Journey <ArrowDownRight size={17} />
            </button>
            <button data-testid="hero-insights-btn" onClick={() => scroll("#insights")} className="btn-ghost">
              Explore My Insights <ArrowUpRight size={16} />
            </button>
          </motion.div>
        </motion.div>

        {/* Right — portrait in clipped frame */}
        <motion.div style={{ y: yPortrait }} className="lg:col-span-5 order-1 lg:order-2" onMouseMove={onMove} onMouseLeave={onLeave}>
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="relative aspect-[5/6] overflow-hidden border border-white/10">
              <img
                src={IMAGES.heroPortrait}
                alt="Dakka Gabriel Rey portrait"
                className="h-full w-full object-cover object-center contrast-[1.05] saturate-[1.08] brightness-[1.03]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            </div>
            {/* corner accents */}
            <div className="absolute -top-3 -left-3 h-16 w-16 border-t border-l border-gold" />
            <div className="absolute -bottom-3 -right-3 h-16 w-16 border-b border-r border-gold" />
            <div className="absolute bottom-5 left-5 z-10">
              <p className="overline text-gold">Est. Mindset</p>
              <p className="font-serif text-xl text-bone mt-1">Knowledge · Character · Progress</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="overline text-ash">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
};
